import React, { useEffect, useState } from 'react';

import Modal from '../common/Modal';

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import { validateRequiredFields } from "../../utils/formValidation";
import { deepTrim } from "../../utils/formValidation";

const WabaDetailsPage = () => {

  const waApi = useWaApi();
  // Only a SYSTEM_ADMIN or ADMIN may change anything here; a USER gets a read-only view. The service refuses
  // the writes too, so this just keeps the controls out of sight.
  // Creating a WABA is narrower still: only a SYSTEM_ADMIN is offered "+ Create New".
  const { canManageWhatsAppSetup, canCreateWabaDetail } = useUserProfileAccess();
  const [allWabas, setAllWabas] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newWaba, setNewWaba] = useState({
    wabaId: '',
    clientPortfolioId: '',
    metaAppId: '',
    status: false,
  });

  const initialFilters = {
    id: '',
    wabaId: '',
    status: null
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const resetFilters = () => {
    clearMessages();
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(0);
  };

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(3);
  const [formError, setFormError] = useState({});
  const [syncingWabaId, setSyncingWabaId] = useState(null);
  const [syncingTemplatesWabaId, setSyncingTemplatesWabaId] = useState(null);
  const [syncingTokenWabaId, setSyncingTokenWabaId] = useState(null);
  const [removingTokenWabaId, setRemovingTokenWabaId] = useState(null);
  const [actionErrors, setActionErrors] = useState({});
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);

  useEffect(() => {

    const fetchWabas = async () => {
      try {
        // WABADetailController only exposes a cached full list, not a paged search,
        // so filtering and pagination for this page are done client-side.
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WABA_DETAILS_ENDPOINT
        );

        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setAllWabas(object || []);
        } else {
          setError(`Error: ${message}`);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching Business Account details:', err);
        setError(err.message || 'An error occurred.');
        setLoading(false);
      }
    };

    const fetchPortfolios = async () => {
      try {
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_CLIENT_PORTFOLIO_DETAILS_ENDPOINT
        );
        const { message, object, httpStatus } = response.data;
        if (httpStatus === 200) {
          setPortfolios(object || []);
        } else {
          setError(`Error: ${message}`);
        }
      } catch (err) {
        console.error('Error fetching client portfolios:', err);
        setError(err.message || 'Failed to load client portfolios.');
      }
    };

    const fetchApps = async () => {
      try {
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_META_APP_DETAILS_ENDPOINT
        );
        const { message, object, httpStatus } = response.data;
        if (httpStatus === 200) {
          setApps(object || []);
        } else {
          setError(`Error: ${message}`);
        }
      } catch (err) {
        console.error('Error fetching apps:', err);
        setError(err.message || 'Failed to load apps.');
      }
    };

    fetchWabas();
    fetchPortfolios();
    fetchApps();
  }, [waApi]);

  useEffect(() => {
    if (modalOpen) {
      setFormError({});
    }
  }, [modalOpen]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;

    setNewWaba((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;

    setFilters((prev) => ({ ...prev, status: statusValue }));
  };

  const applyFilters = () => {
    clearMessages();
    setAppliedFilters(filters);
    setPage(0);
  };

  const filteredWabas = allWabas.filter((waba) => {
    if (appliedFilters.id && String(waba.id) !== String(appliedFilters.id)) return false;
    if (appliedFilters.wabaId && !waba.wabaId?.toLowerCase().includes(appliedFilters.wabaId.toLowerCase())) return false;
    if (appliedFilters.status !== null && waba.status !== appliedFilters.status) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredWabas.length / size));
  const paginatedWabas = filteredWabas.slice(page * size, page * size + size);

  const clearMessages = () => {
    setActionErrors({});
    setSyncMessage(null);
    setCreateError(null);
    setCreateMessage(null);
  };

  const refreshCache = async () => {
    clearMessages();
    try {
      const response = await waApi.get(
        process.env.REACT_APP_REFRESH_WABA_DETAIL_CACHE_ENDPOINT
      );
      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while refreshing the cache.');
    }
  };

  const toggleActivationStatus = async (id, currentStatus) => {
    clearMessages();
    try {
      const newStatus = !currentStatus;
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WABA_DETAIL_STATUS_ENDPOINT}/${id}`,
        { status: newStatus }
      );
      setAllWabas((prev) =>
        prev.map((waba) => waba.id === id ? { ...waba, status: newStatus } : waba)
      );
      setSyncMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while updating the status.' }));
    }
  };

  const handleSyncSendersPhone = async (waba) => {
    clearMessages();
    setSyncingWabaId(waba.wabaId);
    try {
      const response = await waApi.get(
        `${process.env.REACT_APP_SYNC_WABA_ALL_SENDER_PHONE_ENDPOINT}/${waba.wabaId}`
      );
      setSyncMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [waba.id]: err.message || 'An error occurred while syncing sender phones.' }));
    } finally {
      setSyncingWabaId(null);
    }
  };

  const handleSyncTemplates = async (waba) => {
    clearMessages();
    setSyncingTemplatesWabaId(waba.wabaId);
    try {
      const response = await waApi.get(
        `${process.env.REACT_APP_SYNC_WABA_ALL_TEMPLATE_ENDPOINT}/${waba.wabaId}`
      );
      setSyncMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [waba.id]: err.message || 'An error occurred while syncing templates.' }));
    } finally {
      setSyncingTemplatesWabaId(null);
    }
  };

  const handleSyncToken = async (waba) => {
    clearMessages();
    setSyncingTokenWabaId(waba.wabaId);
    try {
      const response = await waApi.get(
        `${process.env.REACT_APP_SYNC_WABA_TOKEN_ENDPOINT}/${waba.wabaId}`
      );
      setSyncMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [waba.id]: err.message || 'An error occurred while syncing the token.' }));
    } finally {
      setSyncingTokenWabaId(null);
    }
  };

  // Drops the WABA's cached access token on the server; "Sync Token" loads a fresh one.
  const handleRemoveToken = async (waba) => {
    clearMessages();
    setRemovingTokenWabaId(waba.wabaId);
    try {
      const response = await waApi.get(
        `${process.env.REACT_APP_REMOVE_WABA_TOKEN_ENDPOINT}/${waba.wabaId}`
      );
      setSyncMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [waba.id]: err.response?.data?.message || err.message || 'An error occurred while removing the token.' }));
    } finally {
      setRemovingTokenWabaId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this WABA detail?')) {
      return;
    }
    setActionErrors((prev) => ({ ...prev, [id]: null }));
    try {
      await waApi.delete(`${process.env.REACT_APP_DELETE_WABA_DETAIL_ENDPOINT}/${id}`);
      setAllWabas((prev) => prev.filter((waba) => waba.id !== id));
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while deleting the WABA detail.' }));
    }
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    clearMessages();
    setFormError({});
    const validation = validateRequiredFields({
      wabaId: { value: newWaba.wabaId, label: "Business Account Id" },
      clientPortfolioId: { value: newWaba.clientPortfolioId, label: "Client Portfolio" },
      metaAppId: { value: newWaba.metaAppId, label: "App" },
    });
    if (Object.keys(validation.errors).length > 0) {
      setFormError(validation.errors);
      return;
    }
    try {
      const payload = deepTrim({
        wabaId: newWaba.wabaId,
        status: newWaba.status,
        waBusinessPortfolio: { id: newWaba.clientPortfolioId },
        waMetaAppDetail: { id: newWaba.metaAppId }
      });
      const response = await waApi.post(
        `${process.env.REACT_APP_SET_WABA_DETAIL_ENDPOINT}`,
        payload
      );
      if (response.data.httpStatus === 200) {
        setAllWabas((prev) => [...prev, response.data.object]);
        setNewWaba({ wabaId: '', clientPortfolioId: '', metaAppId: '', status: true });
        setCreateError(null);
        setCreateMessage(response.data.message);
      } else {
        setCreateMessage(null);
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateMessage(null);
      setCreateError(err.message || 'An error occurred while creating the WABA detail.');
    } finally {
      setModalOpen(false);
    }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  const handleSizeChange = (e) => {
    setSize(Number(e.target.value));
    setPage(0);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading Business Account details: {error}</div>;

  return (
    <div className="page-container">
      <h1>Business Account Details</h1>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>
      <h3>Filters</h3>
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleFilterInputChange}
            placeholder="Only numeric value"
          />
        </div>

        <div className="filter-item">
          <label>Business Account Id:</label>
          <input
            type="text"
            name="wabaId"
            value={filters.wabaId ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusChange} value={filters.status === null ? '' : filters.status ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <button type="button" onClick={applyFilters}>Apply Filters</button>
        <button type="button" onClick={resetFilters}> Reset Filters</button>

      </div>

      {canManageWhatsAppSetup && (
        <div className="action-bar">
          {canCreateWabaDetail && (
            <button
              onClick={() => {
                clearMessages();
                setNewWaba({ wabaId: '', clientPortfolioId: '', metaAppId: '', status: false });
                setModalOpen(true);
              }}
            >
              + Create New
            </button>
          )}

          <button type="button" onClick={refreshCache}>
            Refresh Cache
          </button>
        </div>
      )}

      {createMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      {syncMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {syncMessage}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { clearMessages(); setModalOpen(false); }}>
        <div className="page-container">
          {formError && Object.values(formError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(formError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>
                      {typeof err === "object" ? JSON.stringify(err) : String(err)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit} className="standard-form">
            <h3>Create New WABA Detail</h3>
            <div className="form-group">
              <label>Business Account Id:</label>
              <input
                type="text"
                name="wabaId"
                value={newWaba.wabaId}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Client Portfolio:</label>
              <select
                name="clientPortfolioId"
                value={newWaba.clientPortfolioId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a client portfolio</option>
                {portfolios.map((portfolio) => (
                  <option key={portfolio.id} value={portfolio.id}>
                    {portfolio.clientBusinessPortfolioId} - {portfolio.waClientDetail?.clientName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>App:</label>
              <select
                name="metaAppId"
                value={newWaba.metaAppId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a app</option>
                {apps.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.waAppName} ({app.waAppId})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Activation Status:</label>
              <input
                type="checkbox"
                name="status"
                checked={newWaba.status}
                onChange={handleInputChange}
              />
            </div>
            <button type="submit">Create WABA Detail</button>
          </form>
        </div>
      </Modal>

      <h3>Existing Business Account Details</h3>
      {paginatedWabas.length === 0 ? (
        <p>No WABA details available</p>
      ) : (
        <div className="table-responsive">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Business Account Id</th>
                <th>Client Portfolio Id</th>
                <th>App Name</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Last Updated At</th>
                <th>Last Updated By</th>
                {canManageWhatsAppSetup && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedWabas.map((waba) => (
                <tr key={waba.id}>
                  <td>{waba.id}</td>
                  <td>{waba.wabaId}</td>
                  <td>{waba.waBusinessPortfolio?.clientBusinessPortfolioId}</td>
                  <td>{waba.waMetaAppDetail?.waAppName}</td>
                  <td>{waba.status ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(waba.createdTimestamp).toLocaleString()}</td>
                  <td>{new Date(waba.updatedTimestamp).toLocaleString()}</td>
                  <td>{waba.lastUpdatedBy}</td>
                  {canManageWhatsAppSetup && (
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => toggleActivationStatus(waba.id, waba.status)}>
                          {waba.status ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleSyncSendersPhone(waba)}
                          disabled={syncingWabaId === waba.wabaId}
                        >
                          {syncingWabaId === waba.wabaId ? 'Syncing...' : 'Sync Senders'}
                        </button>
                        <button
                          onClick={() => handleSyncTemplates(waba)}
                          disabled={syncingTemplatesWabaId === waba.wabaId}
                        >
                          {syncingTemplatesWabaId === waba.wabaId ? 'Syncing...' : 'Sync Templates'}
                        </button>
                        <button
                          onClick={() => handleSyncToken(waba)}
                          disabled={syncingTokenWabaId === waba.wabaId}
                        >
                          {syncingTokenWabaId === waba.wabaId ? 'Syncing...' : 'Sync Token'}
                        </button>
                        <button
                          onClick={() => handleRemoveToken(waba)}
                          disabled={removingTokenWabaId === waba.wabaId}
                        >
                          {removingTokenWabaId === waba.wabaId ? 'Removing...' : 'Remove Token'}
                        </button>
                        {/* <button onClick={() => handleDelete(waba.id)}>Delete</button> */}
                      </div>
                      {actionErrors[waba.id] && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                          {actionErrors[waba.id]}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default WabaDetailsPage;
